// This service handles communication with the OpenAI API
import { generateItineraryAPI } from './apiService';

export const generateItinerary = async (tripDetails) => {
  try {
    
    // Ensure valid date objects for startDate and endDate
    const tripDataCopy = { ...tripDetails };
    
    // If startDate or endDate are strings, convert them to Date objects
    if (typeof tripDataCopy.startDate === 'string') {
      tripDataCopy.startDate = new Date(tripDataCopy.startDate);
    }
    
    if (typeof tripDataCopy.endDate === 'string') {
      tripDataCopy.endDate = new Date(tripDataCopy.endDate);
    }
    
    // Check if this is a regeneration
    const isRegeneration = !!tripDetails.itineraryId;
    
    // Generate itinerary via API
    // Pass itineraryId for regeneration
    let result;
    try {
      result = await generateItineraryAPI({
        ...tripDataCopy,
        // Include itineraryId if this is a regeneration
        ...(isRegeneration && { itineraryId: tripDetails.itineraryId })
      });
      
      // Return token balance if included in the response
      return {
        ...result,
        isRegeneration,
        tokenBalance: result.tokenBalance
      };
    } catch (error) {
      // Handle token-related errors
      if (error.message === 'Insufficient tokens to generate a new itinerary') {
        throw {
          message: 'Insufficient tokens to generate a new itinerary',
          tokenRequired: true,
          error
        };
      }
      throw error;
    }
  } catch (error) {
    console.error('Error in generateItinerary:', error);
    // If error is already in the format we want, pass it through
    if (error.tokenRequired) {
      throw error;
    }
    throw new Error(error.message || 'Failed to generate itinerary');
  }
};

// This function generates a mock itinerary for demonstration purposes
// In a real app, this would be replaced with actual OpenAI API responses
const generateMockItinerary = (tripDetails) => {
  const { destination, startDate, endDate, tripDuration, travelers, adults, children, budget, interests } = tripDetails;
  
  // Create a basic structure for the itinerary
  const days = [];
  let startDateObj;
  
  // Ensure we have a proper Date object for the start date
  if (startDate instanceof Date) {
    startDateObj = new Date(startDate);
  } else if (typeof startDate === 'string') {
    // Handle ISO string format (YYYY-MM-DD)
    startDateObj = new Date(startDate);
    // Fix potential timezone issues by setting hours to noon
    startDateObj.setHours(12, 0, 0, 0);
  } else {
    // Fallback to current date if something is wrong
    startDateObj = new Date();
  }
  
  console.log("Mock itinerary startDate:", {
    original: startDate,
    parsed: startDateObj,
    isoString: startDateObj.toISOString()
  });
  
  // Create start and end dates that are based on the user's selection
  // Create the days array with the proper date sequence
  const daysArray = [];
  const actualStartDate = new Date(startDateObj);
  
  console.log("Creating days array with actual dates:", {
    startDate: actualStartDate,
    actualStartDateString: actualStartDate.toISOString()
  });
  
  // Generate the correct day dates based on the actual start date
  for (let i = 0; i < tripDuration; i++) {
    const dayDate = new Date(actualStartDate);
    dayDate.setDate(actualStartDate.getDate() + i);
    // Set to noon to avoid timezone issues
    dayDate.setHours(12, 0, 0, 0);
    
    console.log(`Creating day ${i+1} with date:`, dayDate.toISOString());
    
    daysArray.push({
      day: i + 1,
      date: dayDate,
      activities: generateActivitiesForDestination(destination, i + 1, interests)
    });
  }
  
  // Ensure we have proper Date objects for start and end dates
  let startDateForReturn, endDateForReturn;
  
  if (startDate instanceof Date) {
    startDateForReturn = new Date(startDate);
  } else if (typeof startDate === 'string') {
    startDateForReturn = new Date(startDate);
    startDateForReturn.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
  } else {
    startDateForReturn = new Date();
  }
  
  if (endDate instanceof Date) {
    endDateForReturn = new Date(endDate);
  } else if (typeof endDate === 'string') {
    endDateForReturn = new Date(endDate);
    endDateForReturn.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
  } else {
    // Calculate end date based on start date and duration
    endDateForReturn = new Date(startDateForReturn);
    endDateForReturn.setDate(startDateForReturn.getDate() + tripDuration - 1);
  }
  
  // Log the final dates for debugging
  console.log("Mock itinerary date objects:", {
    startDate: startDateForReturn,
    endDate: endDateForReturn,
    startDateISO: startDateForReturn.toISOString(),
    endDateISO: endDateForReturn.toISOString()
  });
  
  // Preserve all original user inputs in the returned object
  return {
    destination,
    startDate: startDateForReturn,
    endDate: endDateForReturn,
    travelers,
    adults,
    children,
    budget,
    interests: typeof interests === 'string' 
      ? interests.split(',').map(i => i.trim()).filter(i => i !== '')
      : interests,
    title: `Your ${tripDuration}-Day Adventure in ${destination}`,
    summary: `Explore the best of ${destination} with this personalized itinerary designed for your ${tripDuration}-day trip. This plan includes a mix of must-see attractions, local experiences, and time to relax and enjoy your vacation.`,
    days: daysArray,
    tips: [
      "Always carry a map or use a reliable map app",
      "Check the weather forecast daily and dress accordingly",
      "Keep important documents and valuables secure",
      "Try the local cuisine for an authentic experience",
      "Learn a few basic phrases in the local language"
    ],
    travelInfo: {
      visaRequirements: `For travel to ${destination}, visa requirements vary based on your nationality. Check with the local embassy or consulate for the most current information.`,
      travelRestrictions: "Always check for any travel advisories or restrictions before your trip.",
      healthRequirements: "Consider travel insurance that covers medical emergencies. No specific vaccinations are required, but it's always good to be up-to-date on routine vaccinations.",
      currency: `Research the local currency and exchange rates for ${destination} before your trip.`,
      bestTimeToVisit: "The current time period you've selected is generally good for visiting this destination.",
      localCustoms: "Respect local customs and traditions. Research specific cultural norms before your trip."
    },
    // Add a flag to indicate this is mock data
    isMockData: true
  };
};

// Generate mock activities based on destination
const generateActivitiesForDestination = (destination, day, interests) => {
  // This would be replaced by actual OpenAI-generated content
  
  const destinationMap = {
    'Paris, France': [
      { time: '09:00 AM', title: 'Eiffel Tower Visit', description: 'Start your day with a visit to the iconic Eiffel Tower. Consider going early to avoid crowds.' },
      { time: '12:00 PM', title: 'Lunch at a Local Café', description: 'Enjoy a traditional French lunch at a charming café in the 7th arrondissement.' },
      { time: '02:00 PM', title: 'Louvre Museum', description: 'Explore the world-famous Louvre Museum, home to thousands of works of art including the Mona Lisa.' },
      { time: '06:00 PM', title: 'Seine River Cruise', description: 'Take a relaxing cruise along the Seine River to see Paris from a different perspective.' },
      { time: '08:00 PM', title: 'Dinner in Le Marais', description: 'Experience the vibrant Le Marais district and enjoy dinner at a traditional French bistro.' }
    ],
    'Tokyo, Japan': [
      { time: '08:00 AM', title: 'Tsukiji Outer Market', description: 'Start your day with a visit to the lively Tsukiji Outer Market and try some fresh sushi for breakfast.' },
      { time: '10:30 AM', title: 'Meiji Shrine', description: 'Visit the serene Meiji Shrine, a Shinto shrine dedicated to Emperor Meiji and Empress Shoken.' },
      { time: '01:00 PM', title: 'Lunch in Harajuku', description: 'Explore the trendy Harajuku district and enjoy lunch at one of its unique cafés.' },
      { time: '03:00 PM', title: 'Shibuya Crossing', description: 'Experience the famous Shibuya Crossing, one of the busiest pedestrian crossings in the world.' },
      { time: '07:00 PM', title: 'Dinner in Shinjuku', description: 'End your day with dinner in Shinjuku, followed by a visit to the Tokyo Metropolitan Government Building for night views.' }
    ],
    'New York, USA': [
      { time: '09:00 AM', title: 'Central Park Stroll', description: 'Begin with a morning walk through Central Park, visiting Bethesda Fountain and Strawberry Fields.' },
      { time: '11:30 AM', title: 'Metropolitan Museum of Art', description: 'Explore the vast collections at the Met, one of the world\'s largest and finest art museums.' },
      { time: '02:00 PM', title: 'Lunch in Greenwich Village', description: 'Enjoy lunch in the charming Greenwich Village neighborhood.' },
      { time: '04:00 PM', title: 'High Line Walk', description: 'Walk along the High Line, an elevated linear park created on a former railroad track.' },
      { time: '07:00 PM', title: 'Broadway Show', description: 'Experience the magic of Broadway with an evening show.' }
    ]
  };
  
  // Default activities if destination is not in our map
  const defaultActivities = [
    { time: '09:00 AM', title: 'Local Sightseeing', description: `Explore the main attractions in ${destination}.` },
    { time: '12:00 PM', title: 'Lunch Break', description: 'Enjoy local cuisine at a popular restaurant.' },
    { time: '02:00 PM', title: 'Cultural Experience', description: 'Visit a museum or cultural site to learn about local history.' },
    { time: '05:00 PM', title: 'Relaxation Time', description: 'Take some time to relax or shop for souvenirs.' },
    { time: '07:00 PM', title: 'Dinner Experience', description: 'Enjoy dinner at a highly-rated local restaurant.' }
  ];
  
  // Get activities for the destination or use default
  let dailyActivities = destinationMap[destination] || defaultActivities;
  
  // Modify activities slightly based on the day to add variety
  if (day % 3 === 0) {
    dailyActivities = dailyActivities.map(activity => ({
      ...activity,
      title: activity.title.includes('Visit') ? activity.title : `${activity.title} (Special Experience)`,
      description: `${activity.description} Perfect for day ${day} of your trip.`
    }));
  }
  
  // If interests are provided, add a personalized activity
  if (interests && typeof interests === 'string' && interests.trim() !== '') {
    dailyActivities.push({
      time: '04:00 PM',
      title: `Personalized ${interests} Experience`,
      description: `Based on your interest in ${interests}, we've added this special activity just for you.`
    });
    
    // Sort activities by time
    dailyActivities.sort((a, b) => {
      const timeA = new Date(`2000/01/01 ${a.time}`);
      const timeB = new Date(`2000/01/01 ${b.time}`);
      return timeA - timeB;
    });
  }
  
  return dailyActivities;
}; 